import f1, { t_lens } from'../../../pip/library/pi-f1/pi-f1.js';

type t_key = string;
type t_version = 'hrf-1';
type t_unix_timestamp = number;
type t_render = 'none' | 'bar' | 'grid' | 'line' | 'area' | 'pie' | 'doughnut';
type t_render_hint_type = 'metric' | 'color';
type t_hex = string;

/**
 * A valid value from the server is a string, boolean, or number as a raw
 * data type
 */
export type t_value = string | boolean | number;

/**
 * All fields for a single grid presentation
 */
export type TFields = IField[];

/**
 * any object we need for historical purposes
 */
type TArchive = unknown;

export interface IFrontMatter {
  /**
   * a unique key for the report
   */
  id: t_key;
  /**
   * a unique title for the report
   */
  title: string;
  /**
   *
   */
  description: string;
  /**
   * a unique name for the entity being measured
   */
  name: string;
  /**
   *
   */
  timestamp: t_unix_timestamp;
}

/**
 * a field maps to a single column. Every field converts one data type
 * to a presentation type (text) for display in the grid. The type of field
 * defines the data type accepted and configurations for the conversion
 *
 * An IField also contains presentation settings for a column which are specific
 * to the grid itself.
 */
export interface IField {
  /**
   *
   */
  render_type: string;

  /**
   *
   */
  axis: string;

  /**
   *
   *
   * @type {((IGoalInfo | IGoalValue))}
   * @memberof IField
   */
  goal_type: 'bounded' | 'maximum' | 'minimum' | 'informative';

  /**
   *
   */
  goal_value: number | [number, number] | null;

  /**
   * @required
   *
   * The name of the field. This is used for lookup in a dataset and only named fields
   * will be honored. Any property on an object that does not have a named field
   * will be ignored.
   *
   * If there is `abbr` then that field property will be used for sending and receiving
   * properties.
   *
   * format: lowercase and snakecase
   */
  name: string;

  /**
   * Content for a tooltip
   *
   * If provided, then the content will be shown in relation to the field in an appropriate manner.
   *
   * If the field is rendered as:
   * - a column, then a tooltip will be placed on the header
   * - an input, then the tooltip will be placed next to the label
   *
   * @notes
   * - maximum length should be 128 characters
   * - HTML is not valid
   */
  hint: string;

  /**
   * Hidden will usually remove the element entirely from the interface, it will not have any components generated for it.
   * @default false
   */
  hidden: boolean;

  /**
   * order of the column (i.e. field)
   */
  order: number;

  /**
   * Include the value even if it is to be null or undefined
   * @default true
   */
  nullable: boolean;

  /**
   * Content for a block of always-visible text
   *
   * @notes
   * - there is no maximum
   * - this will always be displayed in text-size
   * - HTML is valid
   */
  description: string;

  /**
   * The human-readable version of field or tag, will be auto-generated based on the following logic:
   *
   * 1. First word is capitalized always
   * 2. All -, _, / are replaced with spaces
   * 3. All other words are capitalized if they are greater than three letters
   *
   * @default capitalized(name) the name with underscores split to spaces and capitalized
   */
  title: string;

  /**
   * If the column will not have a consistent data type across all values and requires evaluation for every field
   * to identify the type and render_type of that value
   * @default false
   */
  non_normative: boolean;

  /**
   * if the value is `null`, substitute a hyphen
   * @default '-'
   */
  value_null: t_value;

  /**
   * if the value does not exist at all
   * @default ''
   */
  value_empty: t_value;

  /**
   *
   */
  unit: string;
}

export interface IRenderHintColor {
  /**
   *
   */
  values: t_hex[];
  /**
   *
   */
  labels: string[];
}

export interface IRenderHintMetric {
  /**
   *
   */
  theme: string;
  /**
   *
   */
  header: string;
}

export type TRenderHint = IRenderHintMetric | IRenderHintColor;
export type TRenderHints = Partial<Record<t_render_hint_type, TRenderHint>>;

export interface IRender {
  /**
   *
   */
  render_type: t_render;
  /**
   *
   */
  render_hints: TRenderHints;
}

export type TDatum = Record<string, t_value>;
export type TData = TDatum[];

export interface IHRF {
  version: t_version;
  front_matter: Partial<IFrontMatter>;
  /**
   *
   */
  archive?: TArchive;
  /**
   *
   */
  render: IRender;
  /**
   *
   */
  fields: TFields;
  /**
   *
   */
  data: TData;
}

/**
 * query render hints in a HRF
 * { @link HRFQuery }
 */
export class RenderHint {
  private lens: {
    color: t_lens;
  };

  constructor() {
    this.lens = {
      color: f1.lens('/render/render_hints/color')
    };
  }

  color(hrf: IHRF): IRenderHintColor {
    return f1.read(
      this.lens.color,
      {
        labels: [],
        values: []
      },
      hrf
    );
  }
}

/**
 * query an HRF object
 */
export class HRFQuery {
  readonly renderHint = new RenderHint();
}

export const hrfQuery = new HRFQuery();
export default hrfQuery;
